import { supabase } from "@/lib/supabase";
import { invokeFunction } from "@/lib/api";
import { isSupabaseConfigured } from "@/lib/env";
import { seedJobs, type JobApplyType } from "@/content/jobs";
import type { Department } from "@/features/auth/types";
import type { ApplicationInput } from "@/features/forms/schemas";

/** Department that reviews an applicant: drivers/owner-ops -> Safety; office
 *  takes the applied-to job's department (resolved from seed jobs in preview). */
function resolveDepartment(type: JobApplyType, position?: string | null): Department | null {
  if (type === "driver" || type === "carrier") return "safety";
  if (type === "office" && position) {
    return seedJobs.find((j) => j.slug === position)?.department ?? null;
  }
  return null;
}

export type ApplicationStatus = "new" | "in_review" | "approved" | "rejected";

export interface ApplicationNote {
  id: string;
  applicationId: string;
  authorId: string | null;
  authorName: string;
  authorEmail: string;
  body: string;
  createdAt: string;
  canDelete: boolean;
}

export interface ApplicationPayload {
  position?: string | null;
  interest?: string | null;
  cdlNumber?: string | null;
  cdlState?: string | null;
  experienceYears?: number | null;
  endorsementTanker?: boolean | null;
  endorsementHazmat?: boolean | null;
  endorsementTwic?: boolean | null;
  offloadAirCompressor?: boolean | null;
  offloadPump?: boolean | null;
  authority?: "own" | "lease" | "" | null;
  truck?: string | null;
  mcNumber?: string | null;
  dotNumber?: string | null;
  source?: "google" | "indeed" | "facebook" | "referral" | "truck" | "other" | "" | null;
  referralName?: string | null;
  consentToContact?: boolean | null;
  notes?: string | null;
  resumePath?: string | null;
  internalNotes?: string | null;
  assignedTo?: string | null;
  assignedToName?: string | null;
  assignedToEmail?: string | null;
  assignedAt?: string | null;
  forwardedBy?: string | null;
  forwardNote?: string | null;
}

export interface ApplicationLead {
  id: string;
  type: JobApplyType;
  name: string;
  email: string;
  phone: string;
  status: ApplicationStatus;
  /** Department that reviews this applicant (powers inbox scoping). */
  department: Department | null;
  createdAt: string;
  notes: ApplicationNote[];
  payload: ApplicationPayload;
}

interface ApplicationRow {
  id: string;
  type: JobApplyType;
  name: string;
  email: string;
  phone: string | null;
  status: ApplicationStatus;
  department: Department | null;
  created_at: string;
  payload: ApplicationPayload | null;
  notes?: ApplicationNoteRow[];
}

interface ApplicationNoteRow {
  id: string;
  application_id: string;
  author_id: string | null;
  author_name: string;
  author_email: string | null;
  body: string;
  created_at: string;
  can_delete?: boolean;
}

const LS_KEY = "bb_applications";

const seedApplications: ApplicationLead[] = [
  {
    id: "app-1001",
    type: "driver",
    name: "Chris Lane",
    email: "chris.driver@example.com",
    phone: "+1 (312) 555-0198",
    status: "new",
    department: "safety",
    createdAt: "2026-06-12T14:30:00.000Z",
    notes: [],
    payload: {
      position: "regional-tanker-driver",
      cdlState: "IL",
      experienceYears: 4,
      endorsementTanker: true,
      endorsementHazmat: false,
      source: "indeed",
      consentToContact: true,
      notes: "Interested in regional lanes with weekly home time.",
    },
  },
  {
    id: "app-1002",
    type: "carrier",
    name: "Northbound LLC",
    email: "dispatch@northbound.example",
    phone: "+1 (773) 555-0142",
    status: "in_review",
    department: "safety",
    createdAt: "2026-06-11T18:05:00.000Z",
    notes: [],
    payload: {
      position: "owner-operator",
      authority: "own",
      dotNumber: "1234567",
      mcNumber: "MC-765432",
      truck: "2020 Peterbilt 579",
      experienceYears: 8,
      source: "referral",
      referralName: "Mike S.",
      consentToContact: true,
      notes: "Own tractor, tanker ready.",
    },
  },
  {
    id: "app-1003",
    type: "office",
    name: "Elena Petrova",
    email: "elena.office@example.com",
    phone: "+1 (630) 555-0177",
    status: "new",
    department: "safety",
    createdAt: "2026-06-10T16:15:00.000Z",
    notes: [],
    payload: {
      position: "safety-department-assistant",
      interest: "Safety Department Assistant",
      resumePath: "applications/example-resume.pdf",
      source: "google",
      consentToContact: true,
      notes: "Experience with compliance paperwork and spreadsheets.",
    },
  },
];

const byNewest = (a: ApplicationLead, b: ApplicationLead) =>
  new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

const byNewestNote = (a: ApplicationNote, b: ApplicationNote) =>
  new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

function normalizeReviewDepartment(app: ApplicationLead): ApplicationLead {
  if (app.type === "driver" || app.type === "carrier") {
    return { ...app, department: "safety" };
  }
  return app;
}

function rowToApplication(row: ApplicationRow): ApplicationLead {
  return normalizeReviewDepartment({
    id: row.id,
    type: row.type,
    name: row.name,
    email: row.email,
    phone: row.phone ?? "",
    status: row.status,
    department: row.department ?? null,
    createdAt: row.created_at,
    notes: (row.notes ?? []).map(rowToNote).sort(byNewestNote),
    payload: row.payload ?? {},
  });
}

function rowToNote(row: ApplicationNoteRow): ApplicationNote {
  return {
    id: row.id,
    applicationId: row.application_id,
    authorId: row.author_id,
    authorName: row.author_name,
    authorEmail: row.author_email ?? "",
    body: row.body,
    createdAt: row.created_at,
    canDelete: Boolean(row.can_delete),
  };
}

function localReadAll(): ApplicationLead[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const normalized = (JSON.parse(raw) as ApplicationLead[]).map((app) =>
        normalizeReviewDepartment({
          ...app,
          notes: (app.notes ?? []).sort(byNewestNote),
        }),
      );
      localStorage.setItem(LS_KEY, JSON.stringify(normalized));
      return normalized;
    }
  } catch {
    /* ignore malformed cache */
  }
  localStorage.setItem(LS_KEY, JSON.stringify(seedApplications));
  return [...seedApplications];
}

function localWriteAll(applications: ApplicationLead[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(applications));
}

const supabaseReady = () => isSupabaseConfigured && !!supabase;

const localId = (prefix: string) =>
  `${prefix}-${typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : Date.now()}`;

export async function listApplications(): Promise<ApplicationLead[]> {
  if (supabaseReady()) {
    const { applications } = await invokeFunction<{ applications: ApplicationRow[] }>(
      "applications-admin",
      { action: "list" },
    );
    return applications.map(rowToApplication);
  }
  return localReadAll().sort(byNewest);
}

export async function updateApplication(
  id: string,
  changes: { status?: ApplicationStatus },
): Promise<void> {
  if (supabaseReady()) {
    await invokeFunction("applications-admin", {
      action: "update",
      id,
      ...(changes.status ? { status: changes.status } : {}),
    });
    return;
  }

  const all = localReadAll();
  localWriteAll(
    all.map((app) =>
      app.id === id
        ? {
            ...app,
            status: changes.status ?? app.status,
          }
        : app,
    ),
  );
}

export async function addApplicationNote(id: string, body: string): Promise<void> {
  const noteBody = body.trim();
  if (!noteBody) return;

  if (supabaseReady()) {
    await invokeFunction("applications-admin", {
      action: "addNote",
      id,
      body: noteBody,
    });
    return;
  }

  const now = new Date().toISOString();
  localWriteAll(
    localReadAll().map((app) =>
      app.id === id
        ? {
            ...app,
            notes: [
              {
                id: localId("note"),
                applicationId: id,
                authorId: "dev-internal",
                authorName: "Preview Staff",
                authorEmail: "staff@bullandbearco.com",
                body: noteBody,
                createdAt: now,
                canDelete: true,
              },
              ...app.notes,
            ],
            payload: {
              ...app.payload,
              internalNotes: noteBody,
            },
          }
        : app,
    ),
  );
}

export async function deleteApplicationNote(applicationId: string, noteId: string): Promise<void> {
  if (supabaseReady()) {
    await invokeFunction("applications-admin", {
      action: "deleteNote",
      id: applicationId,
      noteId,
    });
    return;
  }

  localWriteAll(
    localReadAll().map((app) =>
      app.id === applicationId
        ? {
            ...app,
            notes: app.notes.filter((note) => note.id !== noteId || !note.canDelete),
          }
        : app,
    ),
  );
}

export interface ForwardUser {
  id: string;
  email: string;
  fullName: string;
  staffLevel: string | null;
  department: Department | null;
}

interface ForwardUserRow {
  id: string;
  email: string | null;
  full_name: string | null;
  staff_level: string | null;
  department: Department | null;
}

function rowToForwardUser(row: ForwardUserRow): ForwardUser {
  return {
    id: row.id,
    email: row.email ?? "",
    fullName: row.full_name ?? "",
    staffLevel: row.staff_level,
    department: row.department,
  };
}

export async function listApplicationForwardUsers(): Promise<ForwardUser[]> {
  if (supabaseReady()) {
    const { data, error } = await supabase!
      .from("profiles")
      .select("id, email, full_name, staff_level, department")
      .eq("role", "internal")
      .order("full_name", { ascending: true });
    if (error) throw error;
    return (data as ForwardUserRow[]).map(rowToForwardUser);
  }

  return [
    {
      id: "profile-internal",
      email: "staff@bullandbearco.com",
      fullName: "Staff Admin",
      staffLevel: "admin",
      department: null,
    },
    {
      id: "profile-dispatch-mgr",
      email: "dispatcher@bullandbearco.com",
      fullName: "Dispatch Manager",
      staffLevel: "manager",
      department: "dispatch",
    },
    {
      id: "profile-dispatch-zack",
      email: "zack.dispatch@bullandbearco.com",
      fullName: "Zack Dispatcher",
      staffLevel: "member",
      department: "dispatch",
    },
    {
      id: "profile-safety-mgr",
      email: "safety.manager@bullandbearco.com",
      fullName: "Safety Manager",
      staffLevel: "manager",
      department: "safety",
    },
  ];
}

export async function forwardApplication(
  id: string,
  assignedTo: string,
  forwardNote?: string,
): Promise<void> {
  if (supabaseReady()) {
    await invokeFunction("applications-admin", {
      action: "forward",
      id,
      assignedTo,
      forwardNote: forwardNote?.trim() || null,
    });
    return;
  }

  const users = await listApplicationForwardUsers();
  const target = users.find((user) => user.id === assignedTo);
  localWriteAll(
    localReadAll().map((app) =>
      app.id === id
        ? {
            ...app,
            payload: {
              ...app.payload,
              assignedTo,
              assignedToName: target?.fullName || target?.email || "Assigned user",
              assignedToEmail: target?.email ?? "",
              assignedAt: new Date().toISOString(),
              forwardNote: forwardNote?.trim() || null,
            },
          }
        : app,
    ),
  );
}

export async function createLocalApplication(
  values: ApplicationInput,
  resumeFileName?: string,
): Promise<void> {
  const now = new Date().toISOString();
  const lead: ApplicationLead = {
    id: localId("app"),
    type: values.type,
    name: values.name,
    email: values.email,
    phone: values.phone,
    status: "new",
    department: resolveDepartment(values.type, values.position),
    createdAt: now,
    notes: [],
    payload: {
      position: values.position || null,
      interest: values.interest || null,
      cdlNumber: values.cdlNumber || null,
      cdlState: values.cdlState || null,
      experienceYears: values.experienceYears ?? null,
      endorsementTanker: values.endorsementTanker ?? null,
      endorsementHazmat: values.endorsementHazmat ?? null,
      endorsementTwic: values.endorsementTwic ?? null,
      offloadAirCompressor: values.offloadAirCompressor ?? null,
      offloadPump: values.offloadPump ?? null,
      authority: values.authority ?? null,
      truck: values.truck || null,
      mcNumber: values.mcNumber || null,
      dotNumber: values.dotNumber || null,
      source: values.source ?? null,
      referralName: values.referralName || null,
      consentToContact: values.consentToContact ?? null,
      notes: values.notes || null,
      // Preview mode cannot safely store files; keeping the name lets staff see
      // that a resume was attached while Supabase storage is not connected yet.
      resumePath: resumeFileName ? `preview-resume/${resumeFileName}` : null,
    },
  };

  localWriteAll([lead, ...localReadAll()]);
}

/**
 * Permanently delete an application (and its resume file). Goes through the
 * staff-only `applications-admin` edge function in production so the private
 * resume can be removed and department scoping enforced server-side; preview
 * mode deletes from localStorage.
 */
export async function deleteApplication(id: string): Promise<void> {
  if (supabaseReady()) {
    await invokeFunction("applications-admin", { action: "delete", id });
    return;
  }
  localWriteAll(localReadAll().filter((app) => app.id !== id));
}

export async function getResumeUrl(path: string): Promise<string | null> {
  if (!supabaseReady()) return null;
  // `download: true` adds a Content-Disposition: attachment header so the file
  // downloads instead of the browser trying to render it inline (a .docx/.pdf
  // opened in a tab otherwise just shows a blank white page).
  const { data, error } = await supabase!.storage
    .from("documents")
    .createSignedUrl(path, 60 * 5, { download: true });
  if (error) throw error;
  return data.signedUrl;
}
